import React, { useEffect, useState } from "react";
import Pilot from "../../model/Pilot";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormDropDown from "../common/FormDropDown";
import FormInputNumber from "../common/FormInputNumber";
import FormTextArea from "../common/FormTextArea";
import UploadManager from "../../shared/UploadManager";
import FormInputImage from "../common/FormInputImage";
import ImgUtil from "../../utils/ImgUtil";
import UploadFileType from "../../model/enums/UploadFileType";
import SpatialRelation from "../../model/SpatialRelation";
import InclusionType from "../../model/enums/InclusionType";
import _3dInstanceSearchList from "./_3dInstanceSearchList";

interface ISpatialRelationFormErrors {
  transformation: string;
  inclusion_type: string;
  id_included: string;
}

const defaultFormErrors: ISpatialRelationFormErrors = {
  transformation: "",
  inclusion_type: "",
  id_included: "",
};

function SpatialRelationsAddEdit() {
  // read the ID from the url
  const { id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<SpatialRelation>(
    SpatialRelation.EmptyInstance()
  );
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<ISpatialRelationFormErrors>(defaultFormErrors);

  const [searchText, setSearchText] = useState<string>("");

  const [inclusion_type, setInclusionType] = useState<string>(
    InclusionType.PART_OF
  );

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const [uploadManager] = useState(new UploadManager({ apiClient: apiClient }));

  const [selected3DInstanceId, setSelected3DInstanceId] = useState<
    number | null
  >(null);

  const handle3DInstanceSelect = (id: number) => {
    setSelected3DInstanceId(id);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!id) {
      alertsManager.showAlertMessage({
        type: AlertMessageType.ERROR,
        message:
          "No valid ID provided in the URL. Cannot create spatial relation.",
      });
      return;
    }

    if (!selected3DInstanceId) {
      setErrorMessage("Please select a 3D instance.");
      return;
    }

    const newSpatialRelation = new SpatialRelation(
      formData.id_reference,
      selected3DInstanceId,
      authManager.getUserID()!!,
      formData.transformation,
      formData.inclusion_type
    );

    try {
      await apiClient?.post({
        url: APIEndPoints.SPATIAL_RELATIONS.NEW(parseInt(id, 10)), // Ensure id is parsed as a number
        body: newSpatialRelation,
      });
      alertsManager.showAlertMessage({
        type: AlertMessageType.SUCCESS,
        message: "Spatial Relation created successfully",
      });

      navigate(
        SETTINGS.get_path({
          section: AppSections._3DINSTANCES,
          id: parseInt(id!),
          additionalSections: [],
          subsection: AppSubSections.VIEW,
        })
      );
    } catch (error: any) {
      if (!error.processed) {
        processAPIErrors(error);
      }
    }
  };

  const formInputChange = (inputID: string, newValue: string) => {
    if (inputID === "searchText") {
      setSearchText(newValue);
    } else {
      const formDataCopy = JSON.parse(JSON.stringify(formData));
      formDataCopy[inputID] = newValue;
      setFormData(formDataCopy as SpatialRelation);
    }
  };

  const onFormCancel = () => {
    navigate(
      SETTINGS.get_path({
        section: AppSections._3DINSTANCES,
        id: parseInt(id!),
        additionalSections: [],
        subsection: AppSubSections.VIEW,
      })
    );
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again."
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = "Create a new Connected Instance";
  const confirmLabel = "Create Connected Instance";

  const inclusion_type_options = Object.entries(InclusionType).map(
    ([key, value]) => ({
      label: key,
      value: value,
    })
  );

  const handle3dInstanceSelect = (id: number) => {
    console.log("Selected 3D Instance ID:", id);
    setSelected3DInstanceId(id);
    // Handle the selected ID as needed
  };

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
    >
      <FormInputText
        label="Search for Instance to connect:"
        id="searchText"
        value={searchText}
        required={false}
        placeholder="Search"
        onChange={formInputChange}
        error={formErrors.id_included}
      />

      <div className="details-section">
        <div className="details-section-title">
          <h3 className="text-2xl font-bold mb-2">Select Instance:</h3>
        </div>
        <div className="details-section-content">
          <_3dInstanceSearchList
            onSelect={handle3dInstanceSelect}
            parentID={parseInt(id!)}
            searchText={searchText}
            selectedId={selected3DInstanceId}
          />
        </div>
      </div>

      <FormTextArea
        label="Transformation"
        id="transformation"
        value={formData.transformation}
        required={true}
        onChange={formInputChange}
        error={formErrors.transformation}
      />

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      <FormDropDown
        label="Inclusion"
        id="inclusion_type"
        value={formData.inclusion_type}
        required={true}
        options={inclusion_type_options}
        onChange={formInputChange}
        error={formErrors.inclusion_type}
      />

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

export default SpatialRelationsAddEdit;
